import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Building2,
  Euro,
  Banknote,
  Scale,
  CheckCircle2,
  ArrowLeft,
  ArrowRight,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Avatar } from '@/components/ui/avatar';
import { useProperties, type PropertyRow } from '@/hooks/data/useProperties';
import { useClients } from '@/hooks/data/useClients';
import { useLawyers } from '@/hooks/data/useLawyers';
import { useCreateOffer, type CreateOfferPayload } from '@/hooks/data/useOffers';
import { useAuth } from '@/hooks/useAuth';
import { formatDate, formatMoney, formatNumber } from '@/lib/formatLocale';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultPropertyId?: string;
  /** v2 — pre-select the buyer when launching from a Besichtigung row so
   *  the makler doesn't have to repick the same client they were just
   *  viewing. The seller stays unchecked until step 1 because there can
   *  be multiple sellers per property. */
  defaultBuyerId?: string;
}

interface Form {
  // Step 1
  propertyId: string;
  buyerIds: string[];
  sellerIds: string[];
  // Step 2
  offeredPriceEur: string;
  validityDays: string;
  vatApplicable: boolean;
  buyerCommissionType: 'percent' | 'fixed';
  buyerCommissionValue: string;
  sellerCommissionType: 'percent' | 'fixed';
  sellerCommissionValue: string;
  // Step 3
  financingType: '' | 'without_reservation' | 'with_reservation';
  financingDeadline: string;
  viewingDate: string;
  // Step 4
  lawyerId: string;
  lawyerFeePct: string;
  signingPlace: string;
  specialAgreements: string;
  notes: string;
}

const empty = (): Form => ({
  propertyId: '',
  buyerIds: [],
  sellerIds: [],
  offeredPriceEur: '',
  validityDays: '30',
  vatApplicable: false,
  buyerCommissionType: 'percent',
  buyerCommissionValue: '3',
  sellerCommissionType: 'percent',
  sellerCommissionValue: '3',
  financingType: '',
  financingDeadline: '',
  viewingDate: '',
  lawyerId: '',
  lawyerFeePct: '1.5',
  signingPlace: '',
  specialAgreements: '',
  notes: '',
});

interface StepDef {
  id: number;
  title: string;
  icon: React.ReactNode;
}

export function CreateOfferStepper({
  open,
  onOpenChange,
  defaultPropertyId,
  defaultBuyerId,
}: Props) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const create = useCreateOffer();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<Form>(() => ({
    ...empty(),
    propertyId: defaultPropertyId ?? '',
    buyerIds: defaultBuyerId ? [defaultBuyerId] : [],
  }));
  const [err, setErr] = useState<string | null>(null);

  const { data: properties = [] } = useProperties({ marketingStage: 'active' });
  const { data: buyersList = [] } = useClients({ clientType: 'kupac' });
  const { data: sellersList = [] } = useClients({ clientType: 'prodavac' });
  const { data: lawyers = [] } = useLawyers(true);

  const property = useMemo(
    () => properties.find((p) => p.id === form.propertyId),
    [properties, form.propertyId],
  );
  const buyersById = useMemo(() => new Map(buyersList.map((b) => [b.id, b])), [buyersList]);
  const sellersById = useMemo(() => new Map(sellersList.map((s) => [s.id, s])), [sellersList]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((p) => ({ ...p, [k]: v }));

  const reset = () => {
    setForm(empty());
    setStep(1);
    setErr(null);
  };

  const close = () => {
    onOpenChange(false);
    setTimeout(reset, 200);
  };

  const stepValid = (s: number): boolean => {
    if (s === 1) return !!form.propertyId && form.buyerIds.length > 0 && form.sellerIds.length > 0;
    if (s === 2) {
      const price = Number(form.offeredPriceEur);
      return Number.isFinite(price) && price > 0 && Number(form.validityDays) > 0;
    }
    if (s === 3) {
      if (form.financingType === 'with_reservation' && !form.financingDeadline) return false;
      return true;
    }
    if (s === 4) return true;
    return true;
  };

  const next = () => {
    if (!stepValid(step)) {
      setErr(t('offers.stepper.fix_required', 'Bitte Pflichtfelder ausfüllen.'));
      return;
    }
    setErr(null);
    setStep((s) => Math.min(5, s + 1));
  };

  const back = () => {
    setErr(null);
    setStep((s) => Math.max(1, s - 1));
  };

  const submit = async () => {
    setErr(null);
    if (!stepValid(1) || !stepValid(2)) {
      setErr(t('offers.stepper.fix_required', 'Bitte Pflichtfelder ausfüllen.'));
      return;
    }
    const payload: CreateOfferPayload = {
      propertyId: form.propertyId,
      offeredPrice: Math.round(Number(form.offeredPriceEur) * 100),
      validityDays: Number(form.validityDays) || 30,
      vatApplicable: form.vatApplicable,
      lawyerId: form.lawyerId || null,
      lawyerFeePercentage: form.lawyerFeePct ? Number(form.lawyerFeePct) : null,
      buyerCommissionType: form.buyerCommissionType,
      buyerCommissionValue: form.buyerCommissionValue ? Number(form.buyerCommissionValue) : null,
      sellerCommissionType: form.sellerCommissionType,
      sellerCommissionValue: form.sellerCommissionValue
        ? Number(form.sellerCommissionValue)
        : null,
      financingType: form.financingType || null,
      financingDeadline: form.financingDeadline
        ? new Date(form.financingDeadline).getTime()
        : null,
      viewingDate: form.viewingDate ? new Date(form.viewingDate).getTime() : null,
      specialAgreements: form.specialAgreements || null,
      notes: form.notes || null,
      buyerClientIds: form.buyerIds,
      sellerClientIds: form.sellerIds,
    };
    const submitWith = async (extra: Partial<CreateOfferPayload> = {}) => {
      await create.mutateAsync({ ...payload, ...extra });
      toast.success(t('offers.stepper.created', 'Kaufanbot erstellt.'));
      close();
    };
    try {
      await submitWith();
    } catch (cause) {
      const msg = (cause as Error).message;
      if (msg === 'property_sold_no_further_activity') {
        if (profile?.role !== 'admin') {
          setErr(
            t(
              'offers.errors.property_sold',
              'Diese Immobilie ist bereits notariell verkauft — kein neues Kaufanbot mehr möglich.',
            ),
          );
          return;
        }
        const ok = window.confirm(
          t(
            'offers.confirm.override_property_sold',
            '⚠ Diese Immobilie ist bereits notariell verkauft.\n\nAls Admin können Sie das Kaufanbot trotzdem anlegen — die Aktion wird protokolliert.\n\nTrotzdem anlegen?',
          ),
        );
        if (!ok) return;
        try {
          await submitWith({ overridePropertySold: true });
        } catch (cause2) {
          setErr((cause2 as Error).message);
        }
        return;
      }
      setErr(msg);
    }
  };

  const steps: StepDef[] = [
    { id: 1, title: t('offers.stepper.s1', 'Objekt + Parteien'), icon: <Building2 className="h-4 w-4" /> },
    { id: 2, title: t('offers.stepper.s2', 'Preis + Provisionen'), icon: <Euro className="h-4 w-4" /> },
    { id: 3, title: t('offers.stepper.s3', 'Finanzierung'), icon: <Banknote className="h-4 w-4" /> },
    { id: 4, title: t('offers.stepper.s4', 'Rechtliches'), icon: <Scale className="h-4 w-4" /> },
    { id: 5, title: t('offers.stepper.s5', 'Review & Speichern'), icon: <CheckCircle2 className="h-4 w-4" /> },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display">
            {t('offers.stepper.title', 'Befristetes Kaufanbot')}
          </DialogTitle>
        </DialogHeader>

        {/* Progress chips */}
        <div className="flex flex-wrap items-center gap-2">
          {steps.map((s) => {
            const done = step > s.id;
            const current = step === s.id;
            const cls = done ? 'fm-step done' : current ? 'fm-step current' : 'fm-step';
            return (
              <span key={s.id} className={cls}>
                <span className="inline-flex">{s.icon}</span>
                {s.id}. {s.title}
              </span>
            );
          })}
        </div>

        <div className="min-h-[320px] space-y-4">
          {step === 1 && (
            <Step1
              form={form}
              set={set}
              properties={properties}
              buyersList={buyersList}
              sellersList={sellersList}
              property={property}
            />
          )}
          {step === 2 && <Step2 form={form} set={set} />}
          {step === 3 && <Step3 form={form} set={set} />}
          {step === 4 && <Step4 form={form} set={set} lawyers={lawyers} />}
          {step === 5 && (
            <Step5
              form={form}
              property={property}
              buyersById={buyersById}
              sellersById={sellersById}
              lawyers={lawyers}
            />
          )}
        </div>

        {err && (
          <p className="rounded-md border border-[hsl(var(--danger))]/30 bg-[hsl(var(--danger-bg))] px-3 py-2 text-sm text-[hsl(var(--danger))]">
            {err}
          </p>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-line pt-4">
          <Button type="button" variant="outline" onClick={close}>
            {t('common.cancel', 'Abbrechen')}
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={back}
              disabled={step === 1}
              className="gap-1"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t('common.back', 'Zurück')}
            </Button>
            {step < 5 ? (
              <Button type="button" onClick={next} className="gap-1">
                {t('common.next', 'Weiter')}
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button type="button" onClick={submit} disabled={create.isPending} className="gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {create.isPending
                  ? t('common.saving', 'Speichern …')
                  : t('offers.stepper.submit', 'Speichern + Archivieren')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ───── Step 1 ─────
function Step1({
  form,
  set,
  properties,
  buyersList,
  sellersList,
  property,
}: {
  form: Form;
  set: <K extends keyof Form>(k: K, v: Form[K]) => void;
  properties: PropertyRow[];
  buyersList: ReturnType<typeof useClients>['data'] extends infer X
    ? X extends Array<infer R>
      ? R[]
      : never
    : never;
  sellersList: ReturnType<typeof useClients>['data'] extends infer X
    ? X extends Array<infer R>
      ? R[]
      : never
    : never;
  property: PropertyRow | undefined;
}) {
  const { t } = useTranslation();
  const toggle = (key: 'buyerIds' | 'sellerIds', id: string) => {
    set(
      key,
      form[key].includes(id) ? form[key].filter((x) => x !== id) : [...form[key], id],
    );
  };
  return (
    <>
      <div className="space-y-1.5">
        <Label>{t('offers.fields.property', 'Aktive Immobilie')} *</Label>
        <Select
          value={form.propertyId}
          onChange={(e) => {
            const id = e.target.value;
            set('propertyId', id);
            const prop = properties.find((p) => p.id === id);
            if (prop) {
              if (form.sellerIds.length === 0) set('sellerIds', [prop.client_id]);
              if (!form.offeredPriceEur && prop.purchase_price) {
                set('offeredPriceEur', String(prop.purchase_price));
              }
            }
          }}
          required
        >
          <option value="">— auswählen —</option>
          {properties.map((p) => (
            <option key={p.id} value={p.id}>
              {p.address ?? p.city} · {p.edi_real_number ?? ''}
            </option>
          ))}
        </Select>
        {property && (
          <p className="text-xs text-ink-3">
            {property.kg_grundbuch && property.einlagezahl
              ? `${property.kg_grundbuch} · EZ ${property.einlagezahl}`
              : t('offers.stepper.kg_ez_missing', '⚠ KG/Grundbuch oder Einlagezahl fehlen — vor PDF-Erstellung ergänzen.')}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <ParticipantPicker
          label={`${t('offers.fields.buyers', 'Käufer')} *`}
          items={buyersList.map((c) => ({
            id: c.id,
            name: [c.title, c.first_name, c.last_name].filter(Boolean).join(' '),
            phone: c.phone,
          }))}
          selected={form.buyerIds}
          onToggle={(id) => toggle('buyerIds', id)}
        />
        <ParticipantPicker
          label={`${t('offers.fields.sellers', 'Verkäufer')} *`}
          items={sellersList.map((c) => ({
            id: c.id,
            name: [c.title, c.first_name, c.last_name].filter(Boolean).join(' '),
            phone: c.phone,
          }))}
          selected={form.sellerIds}
          onToggle={(id) => toggle('sellerIds', id)}
        />
      </div>
    </>
  );
}

// ───── Step 2 ─────
function Step2({
  form,
  set,
}: {
  form: Form;
  set: <K extends keyof Form>(k: K, v: Form[K]) => void;
}) {
  const { t } = useTranslation();
  const price = Number(form.offeredPriceEur) || 0;
  const grossEstimate = form.vatApplicable
    ? formatMoney((price * 1.2), { style: 'currency', currency: 'EUR' })
    : null;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="space-y-1.5 col-span-2">
        <Label>{t('offers.fields.offered_price', 'Kaufpreis (EUR)')} *</Label>
        <Input
          type="number"
          step="100"
          value={form.offeredPriceEur}
          onChange={(e) => set('offeredPriceEur', e.target.value)}
          placeholder="450000"
          required
        />
        {grossEstimate && (
          <p className="text-xs text-ink-3">brutto inkl. 20 % USt ≈ {grossEstimate}</p>
        )}
      </div>
      <div className="space-y-1.5">
        <Label>{t('offers.fields.validity', 'Gültigkeit (Tage)')} *</Label>
        <Input
          type="number"
          value={form.validityDays}
          onChange={(e) => set('validityDays', e.target.value)}
        />
      </div>
      <div className="flex items-end gap-2">
        <input
          id="vat"
          type="checkbox"
          checked={form.vatApplicable}
          onChange={(e) => set('vatApplicable', e.target.checked)}
          className="h-4 w-4 accent-[hsl(var(--brand-gold))]"
        />
        <Label htmlFor="vat" className="cursor-pointer">
          {t('offers.fields.vat', 'USt anwendbar (+20 %)')}
        </Label>
      </div>

      <div className="col-span-2 mt-2 rounded-xl border border-line bg-paper-2 p-4">
        <h4 className="mb-2 font-display text-base text-ink">
          {t('offers.fields.commissions', 'Provisionen')}
        </h4>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t('offers.fields.buyer_commission', 'Käufer-Provision')}</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                step="0.1"
                value={form.buyerCommissionValue}
                onChange={(e) => set('buyerCommissionValue', e.target.value)}
                className="flex-1"
              />
              <Select
                value={form.buyerCommissionType}
                onChange={(e) =>
                  set('buyerCommissionType', e.target.value as 'percent' | 'fixed')
                }
                className="w-24"
              >
                <option value="percent">%</option>
                <option value="fixed">€ fix</option>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t('offers.fields.seller_commission', 'Verkäufer-Provision')}</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                step="0.1"
                value={form.sellerCommissionValue}
                onChange={(e) => set('sellerCommissionValue', e.target.value)}
                className="flex-1"
              />
              <Select
                value={form.sellerCommissionType}
                onChange={(e) =>
                  set('sellerCommissionType', e.target.value as 'percent' | 'fixed')
                }
                className="w-24"
              >
                <option value="percent">%</option>
                <option value="fixed">€ fix</option>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ───── Step 3 ─────
function Step3({
  form,
  set,
}: {
  form: Form;
  set: <K extends keyof Form>(k: K, v: Form[K]) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="space-y-1.5 col-span-2">
        <Label>{t('offers.fields.financing_type', 'Finanzierungsart')}</Label>
        <Select
          value={form.financingType}
          onChange={(e) => set('financingType', e.target.value as Form['financingType'])}
        >
          <option value="">— ohne Angabe —</option>
          <option value="without_reservation">Ohne Finanzierungsvorbehalt</option>
          <option value="with_reservation">Mit Finanzierungsvorbehalt</option>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label>{t('offers.fields.financing_deadline', 'Finanzierungsfrist')}</Label>
        <Input
          type="date"
          value={form.financingDeadline}
          onChange={(e) => set('financingDeadline', e.target.value)}
          disabled={form.financingType !== 'with_reservation'}
        />
        {form.financingType === 'with_reservation' && !form.financingDeadline && (
          <p className="text-[11px] text-[hsl(var(--warning))]">
            {t('offers.errors.financing_required', 'Pflicht bei „mit Vorbehalt"')}
          </p>
        )}
      </div>
      <div className="space-y-1.5">
        <Label>{t('offers.fields.viewing_date', 'Besichtigungsdatum')}</Label>
        <Input
          type="date"
          value={form.viewingDate}
          onChange={(e) => set('viewingDate', e.target.value)}
        />
      </div>
    </div>
  );
}

// ───── Step 4 ─────
function Step4({
  form,
  set,
  lawyers,
}: {
  form: Form;
  set: <K extends keyof Form>(k: K, v: Form[K]) => void;
  lawyers: ReturnType<typeof useLawyers>['data'] extends infer X
    ? X extends Array<infer R>
      ? R[]
      : never
    : never;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="space-y-1.5 col-span-2">
        <Label>{t('offers.fields.lawyer', 'Vertragserrichter (Anwalt)')}</Label>
        <Select value={form.lawyerId} onChange={(e) => set('lawyerId', e.target.value)}>
          <option value="">— optional —</option>
          {lawyers.map((l) => (
            <option key={l.id} value={l.id}>
              {l.full_name ?? `${l.first_name} ${l.last_name}`}
              {l.law_firm ? ` · ${l.law_firm}` : ''}
            </option>
          ))}
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label>{t('offers.fields.lawyer_fee', 'Anwaltshonorar (%)')}</Label>
        <Input
          type="number"
          step="0.1"
          value={form.lawyerFeePct}
          onChange={(e) => set('lawyerFeePct', e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label>{t('offers.fields.signing_place', 'Unterzeichnungsort')}</Label>
        <Input
          value={form.signingPlace}
          onChange={(e) => set('signingPlace', e.target.value)}
          placeholder="Wien"
        />
      </div>
      <div className="space-y-1.5 col-span-2">
        <Label>{t('offers.fields.special', 'Sondervereinbarungen')}</Label>
        <Textarea
          rows={3}
          value={form.specialAgreements}
          onChange={(e) => set('specialAgreements', e.target.value)}
        />
      </div>
      <div className="space-y-1.5 col-span-2">
        <Label>{t('offers.fields.notes', 'Interne Notizen')}</Label>
        <Textarea
          rows={2}
          value={form.notes}
          onChange={(e) => set('notes', e.target.value)}
        />
      </div>
    </div>
  );
}

// ───── Step 5: Review ─────
function Step5({
  form,
  property,
  buyersById,
  sellersById,
  lawyers,
}: {
  form: Form;
  property: PropertyRow | undefined;
  buyersById: Map<string, { title: string | null; first_name: string | null; last_name: string; phone: string }>;
  sellersById: Map<string, { title: string | null; first_name: string | null; last_name: string; phone: string }>;
  lawyers: Array<{ id: string; full_name: string | null; first_name: string; last_name: string; law_firm: string | null }>;
}) {
  const { t } = useTranslation();
  const lawyer = form.lawyerId ? lawyers.find((l) => l.id === form.lawyerId) : null;
  const price = Number(form.offeredPriceEur) || 0;
  const gross = form.vatApplicable ? price * 1.2 : price;

  const buyerLine = (id: string) => {
    const b = buyersById.get(id);
    if (!b) return id;
    return `${[b.title, b.first_name, b.last_name].filter(Boolean).join(' ')} · ${b.phone}`;
  };
  const sellerLine = (id: string) => {
    const s = sellersById.get(id);
    if (!s) return id;
    return `${[s.title, s.first_name, s.last_name].filter(Boolean).join(' ')} · ${s.phone}`;
  };

  return (
    <div className="space-y-4">
      <ReviewCard title={t('offers.review.object', 'Objekt')}>
        <p className="font-medium text-ink">{property?.address ?? '—'}</p>
        <p className="text-sm text-ink-3">
          {property?.city}
          {property?.postal_code ? ` · ${property.postal_code}` : ''}
          {property?.edi_real_number ? ` · EDI ${property.edi_real_number}` : ''}
        </p>
        {property?.kg_grundbuch && (
          <p className="text-xs text-ink-3">
            {property.kg_grundbuch} · EZ {property.einlagezahl ?? '—'}
            {property.b_laufnummer ? ` · B-Lfd ${property.b_laufnummer}` : ''}
          </p>
        )}
      </ReviewCard>

      <div className="grid gap-3 md:grid-cols-2">
        <ReviewCard title={t('offers.review.buyers', 'Käufer ({{count}})', { count: form.buyerIds.length })}>
          {form.buyerIds.length === 0 ? (
            <p className="text-sm text-ink-3">— keine —</p>
          ) : (
            <ul className="space-y-1">
              {form.buyerIds.map((id) => {
                const b = buyersById.get(id);
                const name = b
                  ? [b.title, b.first_name, b.last_name].filter(Boolean).join(' ')
                  : id;
                return (
                  <li key={id} className="flex items-center gap-2 text-sm">
                    <Avatar name={name} size="sm" />
                    <span className="text-ink-2 truncate">{buyerLine(id)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </ReviewCard>
        <ReviewCard title={t('offers.review.sellers', 'Verkäufer ({{count}})', { count: form.sellerIds.length })}>
          {form.sellerIds.length === 0 ? (
            <p className="text-sm text-ink-3">— keine —</p>
          ) : (
            <ul className="space-y-1">
              {form.sellerIds.map((id) => {
                const s = sellersById.get(id);
                const name = s
                  ? [s.title, s.first_name, s.last_name].filter(Boolean).join(' ')
                  : id;
                return (
                  <li key={id} className="flex items-center gap-2 text-sm">
                    <Avatar name={name} size="sm" />
                    <span className="text-ink-2 truncate">{sellerLine(id)}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </ReviewCard>
      </div>

      <ReviewCard title={t('offers.review.price', 'Preis & Provisionen')}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <KV label="Kaufpreis" value={formatMoney(price)} />
          <KV
            label="USt"
            value={
              form.vatApplicable
                ? `+ 20 % → ${formatMoney(gross, { maximumFractionDigits: 0 })}`
                : 'nicht anwendbar'
            }
          />
          <KV label={t('offers.review.validity', 'Gültigkeit')} value={`${form.validityDays} Tage`} />
          <KV
            label={t('offers.buyerCommission', 'Käufer-Provision')}
            value={`${form.buyerCommissionValue} ${form.buyerCommissionType === 'percent' ? '%' : '€ fix'}`}
          />
          <KV
            label={t('offers.sellerCommission', 'Verkäufer-Provision')}
            value={`${form.sellerCommissionValue} ${form.sellerCommissionType === 'percent' ? '%' : '€ fix'}`}
          />
        </div>
      </ReviewCard>

      <ReviewCard title={t('offers.review.financing', 'Finanzierung & Termine')}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <KV
            label="Finanzierungsart"
            value={
              form.financingType === 'with_reservation'
                ? 'Mit Finanzierungsvorbehalt'
                : form.financingType === 'without_reservation'
                  ? 'Ohne Vorbehalt'
                  : '—'
            }
          />
          <KV
            label="Finanzierungsfrist"
            value={form.financingDeadline ? formatDate(new Date(form.financingDeadline)) : '—'}
          />
          <KV
            label="Besichtigungsdatum"
            value={form.viewingDate ? formatDate(new Date(form.viewingDate)) : '—'}
          />
        </div>
      </ReviewCard>

      <ReviewCard title={t('offers.review.legal', 'Rechtliches')}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <KV
            label="Vertragserrichter"
            value={
              lawyer
                ? `${lawyer.full_name ?? `${lawyer.first_name} ${lawyer.last_name}`}${lawyer.law_firm ? ` · ${lawyer.law_firm}` : ''}`
                : '—'
            }
          />
          <KV label="Anwaltshonorar" value={`${form.lawyerFeePct} %`} />
          <KV label="Unterzeichnungsort" value={form.signingPlace || '—'} />
        </div>
        {form.specialAgreements && (
          <div className="mt-2">
            <p className="text-xs uppercase tracking-[0.08em] text-ink-3">Sondervereinbarungen</p>
            <p className="mt-1 whitespace-pre-line text-sm text-ink-2">{form.specialAgreements}</p>
          </div>
        )}
      </ReviewCard>

      <p className="rounded-md border border-[hsl(var(--info))]/30 bg-[hsl(var(--info-bg))] px-3 py-2 text-xs text-[hsl(var(--info))]">
        {t(
          'offers.review.signing_note',
          'Nach „Speichern + Archivieren" können Käufer und Verkäufer das Anbot in der Detailansicht unterschreiben (Signature-Pad oder Magic-Link folgt).',
        )}
      </p>
    </div>
  );
}

function ReviewCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-line bg-card p-4">
      <h4 className="mb-2 font-display text-sm uppercase tracking-[0.08em] text-ink-3">{title}</h4>
      {children}
    </div>
  );
}

function KV({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.08em] text-ink-3">{label}</p>
      <p className="text-sm text-ink">{value}</p>
    </div>
  );
}

function ParticipantPicker({
  label,
  items,
  selected,
  onToggle,
}: {
  label: string;
  items: Array<{ id: string; name: string; phone: string }>;
  selected: string[];
  onToggle: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items.slice(0, 50);
    return items
      .filter(
        (it) =>
          it.name.toLowerCase().includes(s) || it.phone.includes(s),
      )
      .slice(0, 50);
  }, [items, search]);
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t('common.search_placeholder', 'Suchen …')}
        className="w-full rounded-md border border-line bg-card px-2 py-1 text-xs"
      />
      <div className="max-h-44 overflow-y-auto rounded-md border border-line bg-paper-2 p-1.5">
        {filtered.length === 0 ? (
          <p className="px-2 py-1 text-xs text-ink-3">— keine —</p>
        ) : (
          filtered.map((it) => {
            const on = selected.includes(it.id);
            return (
              <button
                key={it.id}
                type="button"
                onClick={() => onToggle(it.id)}
                className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-start text-xs transition ${
                  on ? 'bg-ink text-paper' : 'hover:bg-paper-3 text-ink-2'
                }`}
              >
                <span className="truncate">
                  <span className="font-medium">{it.name || '—'}</span>
                  <span className="ms-1 font-mono text-[10px] opacity-70">{it.phone}</span>
                </span>
                <span className={on ? 'opacity-100' : 'opacity-30'}>✓</span>
              </button>
            );
          })
        )}
      </div>
      {selected.length > 0 && (
        <p className="text-[11px] text-ink-3">{selected.length} ausgewählt</p>
      )}
    </div>
  );
}
